// src/invites.ts
async function validateInvite(apiUrl, token) {
  try {
    const response = await fetch(`${apiUrl}/orgs/invites/${token}/validate`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json"
      }
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        valid: false,
        error: errorData.detail || errorData.message || "Invalid or expired invitation"
      };
    }
    const data = await response.json();
    const invite = {
      id: data.id,
      organizationId: data.organization_id,
      organizationSlug: data.organization_slug,
      organizationName: data.organization_name,
      email: data.email,
      roles: data.roles || [],
      expiresAt: data.expires_at,
      invitedBy: {
        id: data.invited_by?.id || data.invited_by_id,
        name: data.invited_by?.name || null,
        email: data.invited_by?.email || ""
      }
    };
    return {
      valid: true,
      invite
    };
  } catch (error) {
    return {
      valid: false,
      error: error instanceof Error ? error.message : "Failed to validate invitation"
    };
  }
}
async function acceptInvite(apiUrl, token, accessToken) {
  try {
    const response = await fetch(`${apiUrl}/orgs/invites/${token}/accept`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`
      }
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.detail || errorData.message || "Failed to accept invitation"
      };
    }
    const data = await response.json();
    return {
      success: true,
      organizationId: data.organization_id,
      organizationSlug: data.organization_slug
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to accept invitation"
    };
  }
}

// src/pkce.ts
function generateCodeVerifier(length = 64) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join("");
}
async function generateCodeChallenge(verifier, method = "S256") {
  if (method === "plain") {
    return verifier;
  }
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(hash);
}
function generateState() {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64UrlEncode(array.buffer);
}
function base64UrlEncode(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
async function generatePKCE() {
  const verifier = generateCodeVerifier();
  const challenge = await generateCodeChallenge(verifier, "S256");
  return { verifier, challenge, method: "S256" };
}

// src/token.ts
var REFRESH_TOKEN_KEY = "usoftware_refresh_token";
var TokenStore = class {
  constructor(clientId) {
    this.accessToken = null;
    this.idToken = null;
    this.expiresAt = null;
    this.scope = null;
    this.storageKey = clientId ? `${REFRESH_TOKEN_KEY}_${clientId}` : REFRESH_TOKEN_KEY;
  }
  /**
   * Store tokens from a token response.
   * Access/ID tokens go to memory, refresh token goes to localStorage.
   */
  setTokens(tokens) {
    this.accessToken = tokens.access_token;
    this.idToken = tokens.id_token ?? null;
    this.scope = tokens.scope;
    this.expiresAt = Date.now() + tokens.expires_in * 1e3;
    if (tokens.refresh_token) {
      this.setRefreshToken(tokens.refresh_token);
    }
  }
  /**
   * Get the current access token if valid.
   */
  getAccessToken() {
    if (!this.accessToken) return null;
    if (this.isExpired()) return null;
    return this.accessToken;
  }
  /**
   * Get the current ID token.
   */
  getIdToken() {
    return this.idToken;
  }
  /**
   * Get the refresh token from localStorage.
   */
  getRefreshToken() {
    try {
      return localStorage.getItem(this.storageKey);
    } catch {
      return null;
    }
  }
  /**
   * Set the refresh token in localStorage.
   */
  setRefreshToken(token) {
    try {
      localStorage.setItem(this.storageKey, token);
    } catch {
      console.warn("localStorage not available, refresh token will not persist");
    }
  }
  /**
   * Check if the access token is expired or about to expire.
   */
  isExpired(bufferSeconds = 30) {
    if (!this.expiresAt) return true;
    return Date.now() >= this.expiresAt - bufferSeconds * 1e3;
  }
  /**
   * Get the current scopes.
   */
  getScope() {
    return this.scope;
  }
  /**
   * Check if we have a stored refresh token (for session recovery).
   */
  hasRefreshToken() {
    return this.getRefreshToken() !== null;
  }
  /**
   * Clear all stored tokens (memory + localStorage).
   */
  clear() {
    this.accessToken = null;
    this.idToken = null;
    this.expiresAt = null;
    this.scope = null;
    try {
      localStorage.removeItem(this.storageKey);
    } catch {
    }
  }
};
function decodeJwt(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const padded = payload + "=".repeat((4 - payload.length % 4) % 4);
    const decoded = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}
function isJwtExpired(token, bufferSeconds = 30) {
  const claims = decodeJwt(token);
  if (!claims?.exp) return true;
  const expiresAt = claims.exp * 1e3;
  return Date.now() >= expiresAt - bufferSeconds * 1e3;
}
function extractUserFromIdToken(idToken) {
  const claims = decodeJwt(idToken);
  if (!claims?.sub) return null;
  return {
    id: claims.sub,
    email: claims.email ?? null,
    emailVerified: claims.email_verified ?? false,
    username: claims.preferred_username ?? null,
    displayName: claims.name ?? null,
    picture: claims.picture ?? null,
    avatarUrl: claims.picture ?? null
  };
}
function extractAppUserFromAccessToken(accessToken) {
  const claims = decodeJwt(accessToken);
  if (!claims?.app_user_id || !claims?.app_id) return null;
  return {
    id: claims.app_user_id,
    userId: claims.sub ?? "",
    appId: claims.app_id,
    roles: claims.roles ?? [],
    scopes: claims.scopes ?? [],
    organizationId: claims.org_id ?? null,
    organizationSlug: claims.org_slug ?? null,
    organizationRoles: claims.org_roles ?? null
  };
}

// src/state.ts
var AuthStateManager = class {
  constructor() {
    this.state = { status: "loading" };
    this.listeners = /* @__PURE__ */ new Set();
  }
  /**
   * Get the current auth state.
   */
  getState() {
    return this.state;
  }
  /**
   * Set the auth state to loading.
   */
  setLoading() {
    this.setState({ status: "loading" });
  }
  /**
   * Set the auth state to unauthenticated.
   */
  setUnauthenticated() {
    this.setState({ status: "unauthenticated" });
  }
  /**
   * Set the auth state to authenticated.
   */
  setAuthenticated(user, appUser) {
    this.setState({ status: "authenticated", user, appUser });
  }
  /**
   * Subscribe to auth state changes.
   */
  subscribe(listener) {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }
  setState(state) {
    this.state = state;
    this.notifyListeners();
  }
  notifyListeners() {
    this.listeners.forEach((listener) => listener(this.state));
  }
};

// src/popup.ts
var DEFAULT_WIDTH = 500;
var DEFAULT_HEIGHT = 600;
function openPopup(url, options = {}) {
  const { width = DEFAULT_WIDTH, height = DEFAULT_HEIGHT } = options;
  const left = window.screenX + (window.innerWidth - width) / 2;
  const top = window.screenY + (window.innerHeight - height) / 2;
  const features = [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    "menubar=no",
    "toolbar=no",
    "location=yes",
    "status=no",
    "resizable=yes",
    "scrollbars=yes"
  ].join(",");
  return new Promise((resolve) => {
    const popup = window.open(url, "usoftware_popup", features);
    if (!popup) {
      resolve({
        success: false,
        error: "Popup blocked. Please allow popups and try again."
      });
      return;
    }
    const checkClosed = setInterval(() => {
      if (popup.closed) {
        clearInterval(checkClosed);
        window.removeEventListener("message", handleMessage);
        resolve({ success: false, error: "Popup closed by user" });
      }
    }, 500);
    const handleMessage = (event) => {
      if (event.source !== popup) return;
      if (event.data?.type === "stepup_response" || event.data?.type === "billing_response") {
        clearInterval(checkClosed);
        window.removeEventListener("message", handleMessage);
        popup.close();
        if (event.data.error) {
          resolve({ success: false, error: event.data.error });
        } else {
          resolve({ success: true, token: event.data.token });
        }
      }
    };
    window.addEventListener("message", handleMessage);
  });
}
function openStepUpChallengePopup(baseUrl, challengeId) {
  const url = new URL(`/stepup/${challengeId}`, baseUrl);
  return openPopup(url.toString());
}
function openStepUpPopup(baseUrl, action, paramsHash, level = "any_factor") {
  const url = new URL("/stepup", baseUrl);
  url.searchParams.set("action", action);
  url.searchParams.set("params_hash", paramsHash);
  url.searchParams.set("level", level);
  return openPopup(url.toString());
}
function openAgreementPopup(baseUrl, agreementId) {
  const url = new URL("/billing/agree", baseUrl);
  url.searchParams.set("id", agreementId);
  return openPopup(url.toString());
}

// src/client.ts
var PKCE_STORAGE_KEY = "usoftware_pkce";
var STATE_STORAGE_KEY = "usoftware_state";
var SESSION_ID_STORAGE_KEY = "usoftware_session_id";
var USoftwareClient = class {
  constructor(config) {
    this.orgSlug = null;
    const baseUrl = config.baseUrl || "https://core.u.software";
    const apiUrl = config.apiUrl || (baseUrl.includes("core.") ? baseUrl.replace("core.", "api.") : baseUrl);
    this.config = {
      baseUrl,
      apiUrl,
      scopes: ["openid", "profile", "email"],
      ...config
    };
    this.tokenStore = new TokenStore(config.clientId);
    this.stateManager = new AuthStateManager();
  }
  /**
   * Get a namespaced storage key.
   * Appends the organization slug when present to isolate storage per org.
   *
   * @param key - Base storage key
   * @returns Namespaced key (e.g., 'usoftware_pkce_acme-corp' or 'usoftware_pkce')
   */
  getStorageKey(key) {
    if (this.orgSlug) {
      return `${key}_${this.orgSlug}`;
    }
    return key;
  }
  /**
   * Set the organization context for storage namespacing.
   * Call this when the user switches organizations.
   *
   * @param orgSlug - Organization slug, or null to clear org context
   */
  setOrgContext(orgSlug) {
    this.orgSlug = orgSlug;
  }
  /**
   * Get the current organization context.
   *
   * @returns Current organization slug, or null if not in org context
   */
  getOrgContext() {
    return this.orgSlug;
  }
  /**
   * Redirect to login page
   *
   * Uses secure OAuth flow with server-side sessions:
   * 1. Creates auth session via POST /oauth/sessions
   * 2. Stores session_id, PKCE verifier, and state locally
   * 3. Redirects to /authorize with session_id
   *
   * This prevents direct URL manipulation and validates all
   * parameters server-side at session creation.
   */
  async login(options) {
    const scopes = options?.scopes ? [...this.config.scopes, ...options.scopes] : this.config.scopes;
    const state = generateState();
    const { verifier, challenge, method } = await generatePKCE();
    try {
      const sessionResponse = await fetch(`${this.config.apiUrl}/oauth/sessions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          client_id: this.config.clientId,
          redirect_uri: this.config.redirectUri,
          scope: scopes.join(" "),
          state,
          code_challenge: challenge,
          code_challenge_method: method,
          prompt: options?.prompt
        })
      });
      if (!sessionResponse.ok) {
        const error = await sessionResponse.json().catch(() => ({}));
        throw new Error(error.detail || "Failed to create auth session");
      }
      const session = await sessionResponse.json();
      sessionStorage.setItem(this.getStorageKey(PKCE_STORAGE_KEY), verifier);
      sessionStorage.setItem(this.getStorageKey(STATE_STORAGE_KEY), state);
      sessionStorage.setItem(this.getStorageKey(SESSION_ID_STORAGE_KEY), session.session_id);
      const url = new URL("/authorize", this.config.baseUrl);
      url.searchParams.set("response_type", "code");
      url.searchParams.set("client_id", this.config.clientId);
      url.searchParams.set("redirect_uri", this.config.redirectUri);
      url.searchParams.set("scope", scopes.join(" "));
      url.searchParams.set("state", state);
      url.searchParams.set("code_challenge", challenge);
      url.searchParams.set("code_challenge_method", method);
      url.searchParams.set("session_id", session.session_id);
      if (options?.prompt) {
        url.searchParams.set("prompt", options.prompt);
      }
      window.location.href = url.toString();
    } catch (error) {
      console.warn("Failed to create auth session, using legacy flow:", error);
      sessionStorage.setItem(this.getStorageKey(PKCE_STORAGE_KEY), verifier);
      sessionStorage.setItem(this.getStorageKey(STATE_STORAGE_KEY), state);
      const url = new URL("/authorize", this.config.baseUrl);
      url.searchParams.set("response_type", "code");
      url.searchParams.set("client_id", this.config.clientId);
      url.searchParams.set("redirect_uri", this.config.redirectUri);
      url.searchParams.set("scope", scopes.join(" "));
      url.searchParams.set("state", state);
      url.searchParams.set("code_challenge", challenge);
      url.searchParams.set("code_challenge_method", method);
      if (options?.prompt) {
        url.searchParams.set("prompt", options.prompt);
      }
      window.location.href = url.toString();
    }
  }
  /**
   * Log out the current user
   *
   * @param options.returnTo - URL to redirect after logout
   * @param options.global - Also logout from u.software platform session (not just this app)
   *
   * Flow:
   * 1. Revoke the app's refresh token via /oauth/revoke
   * 2. Clear local tokens
   * 3. If global=true, redirect to core.u.software/logout to clear platform session
   * 4. Otherwise, redirect to returnTo if specified
   */
  async logout(options) {
    const refreshToken = this.tokenStore.getRefreshToken();
    if (refreshToken) {
      try {
        await fetch(`${this.config.apiUrl}/oauth/revoke`, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({
            token: refreshToken,
            token_type_hint: "refresh_token",
            client_id: this.config.clientId
          })
        });
      } catch {
      }
    }
    this.tokenStore.clear();
    this.stateManager.setUnauthenticated();
    if (options?.global) {
      const returnTo = options.returnTo || window.location.origin;
      window.location.href = `${this.config.baseUrl}/logout?return_to=${encodeURIComponent(returnTo)}`;
      return;
    }
    if (options?.returnTo) {
      window.location.href = options.returnTo;
    }
  }
  /**
   * Handle OAuth callback
   *
   * This method is idempotent - if called multiple times (e.g., from both
   * AuthProvider and CallbackPage), subsequent calls will return the
   * already-authenticated user or null.
   */
  async handleCallback() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");
    const error = params.get("error");
    if (error) {
      throw new Error(params.get("error_description") || error);
    }
    if (!code) {
      if (this.isAuthenticated()) {
        return this.getUser();
      }
      throw new Error("No authorization code received");
    }
    const savedState = sessionStorage.getItem(this.getStorageKey(STATE_STORAGE_KEY));
    const verifier = sessionStorage.getItem(this.getStorageKey(PKCE_STORAGE_KEY));
    if (!savedState || !verifier) {
      if (this.isAuthenticated()) {
        return this.getUser();
      }
      return null;
    }
    if (state !== savedState) {
      throw new Error("Invalid state parameter");
    }
    sessionStorage.removeItem(this.getStorageKey(PKCE_STORAGE_KEY));
    sessionStorage.removeItem(this.getStorageKey(STATE_STORAGE_KEY));
    sessionStorage.removeItem(this.getStorageKey(SESSION_ID_STORAGE_KEY));
    const user = await this.exchangeCode(code, verifier);
    window.history.replaceState({}, "", window.location.pathname);
    return user;
  }
  /**
   * Check for existing session and restore it if possible.
   *
   * This method checks for a stored refresh token in localStorage and
   * uses it to obtain new access tokens. Call this on app startup to
   * restore the user's session after a page refresh.
   *
   * Flow:
   * 1. Check localStorage for refresh_token
   * 2. If found, call /oauth/token with grant_type=refresh_token
   * 3. Store new tokens (including rotated refresh_token)
   * 4. Fetch user info from /oauth/userinfo
   * 5. Return authenticated user
   *
   * @returns User if session restored, null if no valid session
   */
  async checkSession() {
    this.stateManager.setLoading();
    if (!this.tokenStore.hasRefreshToken()) {
      this.stateManager.setUnauthenticated();
      return null;
    }
    try {
      const refreshed = await this.refreshAccessToken();
      if (!refreshed) {
        this.tokenStore.clear();
        this.stateManager.setUnauthenticated();
        return null;
      }
      const accessToken = this.tokenStore.getAccessToken();
      const appUser = accessToken ? extractAppUserFromAccessToken(accessToken) : null;
      if (appUser?.organizationSlug) {
        this.setOrgContext(appUser.organizationSlug);
      }
      const user = await this.fetchUserInfo();
      if (user) {
        this.stateManager.setAuthenticated(user, appUser);
        return user;
      }
      this.stateManager.setUnauthenticated();
      return null;
    } catch {
      this.tokenStore.clear();
      this.stateManager.setUnauthenticated();
      return null;
    }
  }
  /**
   * Fetch user info from the /oauth/userinfo endpoint.
   */
  async fetchUserInfo() {
    const accessToken = this.tokenStore.getAccessToken();
    if (!accessToken) return null;
    try {
      const response = await fetch(`${this.config.apiUrl}/oauth/userinfo`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${accessToken}`
        }
      });
      if (!response.ok) return null;
      const data = await response.json();
      return {
        id: data.sub,
        email: data.email ?? null,
        emailVerified: data.email_verified ?? false,
        username: data.preferred_username ?? null,
        displayName: data.name ?? null,
        picture: data.picture ?? null,
        avatarUrl: data.picture ?? null
      };
    } catch {
      return null;
    }
  }
  /**
   * @deprecated Use checkSession() instead. Silent login via iframe
   * no longer works reliably due to third-party cookie restrictions.
   */
  async silentLogin() {
    console.warn(
      "silentLogin() is deprecated. Use checkSession() instead. The iframe-based approach no longer works reliably due to browser cookie restrictions."
    );
    return this.checkSession();
  }
  /**
   * Exchange authorization code for tokens
   */
  async exchangeCode(code, verifier, redirectUri) {
    const response = await fetch(`${this.config.apiUrl}/oauth/token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded"
      },
      body: new URLSearchParams({
        grant_type: "authorization_code",
        code,
        redirect_uri: redirectUri || this.config.redirectUri,
        client_id: this.config.clientId,
        code_verifier: verifier
      })
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error_description || "Token exchange failed");
    }
    const tokens = await response.json();
    this.tokenStore.setTokens(tokens);
    const user = tokens.id_token ? extractUserFromIdToken(tokens.id_token) : null;
    const appUser = extractAppUserFromAccessToken(tokens.access_token);
    if (appUser?.organizationSlug) {
      this.setOrgContext(appUser.organizationSlug);
    }
    if (user) {
      this.stateManager.setAuthenticated(user, appUser);
    } else {
      this.stateManager.setUnauthenticated();
    }
    return user;
  }
  /**
   * Refresh the access token
   */
  async refreshAccessToken() {
    const refreshToken = this.tokenStore.getRefreshToken();
    if (!refreshToken) return false;
    try {
      const response = await fetch(`${this.config.apiUrl}/oauth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded"
        },
        body: new URLSearchParams({
          grant_type: "refresh_token",
          refresh_token: refreshToken,
          client_id: this.config.clientId
        })
      });
      if (!response.ok) return false;
      const tokens = await response.json();
      this.tokenStore.setTokens(tokens);
      return true;
    } catch {
      return false;
    }
  }
  /**
   * Get current access token (refreshing if needed)
   */
  async getAccessToken() {
    let token = this.tokenStore.getAccessToken();
    if (!token || this.tokenStore.isExpired()) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        token = this.tokenStore.getAccessToken();
      }
    }
    return token;
  }
  /**
   * Get current user
   */
  getUser() {
    const state = this.stateManager.getState();
    if (state.status === "authenticated") {
      return state.user;
    }
    return null;
  }
  /**
   * Get current AppUser
   */
  getAppUser() {
    const state = this.stateManager.getState();
    if (state.status === "authenticated") {
      return state.appUser;
    }
    return null;
  }
  /**
   * Check if user has a specific scope
   */
  hasScope(scope) {
    const appUser = this.getAppUser();
    if (!appUser) return false;
    return appUser.scopes.includes(scope);
  }
  /**
   * Check if user has a specific role
   */
  hasRole(role) {
    const appUser = this.getAppUser();
    if (!appUser) return false;
    return appUser.roles.includes(role);
  }
  /**
   * Get all scopes
   */
  getScopes() {
    return this.getAppUser()?.scopes ?? [];
  }
  /**
   * Get all roles
   */
  getRoles() {
    return this.getAppUser()?.roles ?? [];
  }
  /**
   * Check if user is authenticated
   */
  isAuthenticated() {
    return this.stateManager.getState().status === "authenticated";
  }
  /**
   * Check if user is an AppUser (has app context)
   */
  isAppUser() {
    const state = this.stateManager.getState();
    return state.status === "authenticated" && state.appUser !== null;
  }
  /**
   * Check if auth state is still loading
   */
  isLoading() {
    return this.stateManager.getState().status === "loading";
  }
  /**
   * Subscribe to auth state changes
   */
  onAuthChange(callback) {
    return this.stateManager.subscribe(callback);
  }
  /**
   * Open a popup window (for step-up, billing)
   */
  async openPopup(url, options) {
    return openPopup(url, options);
  }
  /**
   * Open step-up verification popup with a challenge ID.
   * This is the primary method for step-up verification.
   *
   * Flow:
   * 1. App backend calls POST /internal/stepup/create to get challengeId
   * 2. App frontend calls this method with challengeId
   * 3. User verifies in popup
   * 4. Returns token for app backend to validate
   */
  async openStepUpChallenge(challengeId) {
    return openStepUpChallengePopup(this.config.baseUrl, challengeId);
  }
  /**
   * Open step-up authentication popup
   * @deprecated Use openStepUpChallenge with a challenge ID instead
   */
  async requestStepUp(action, paramsHash, level) {
    return openStepUpPopup(this.config.baseUrl, action, paramsHash, level);
  }
  /**
   * Open billing agreement popup
   */
  async requestAgreement(agreementId) {
    return openAgreementPopup(this.config.baseUrl, agreementId);
  }
  // =============================================================================
  // Organization Auth Methods
  // =============================================================================
  /**
   * Login to an organization context via redirect.
   * This redirects to the org-specific login page.
   *
   * @param orgSlug - Organization slug to login to
   * @param options - Login options (force, scopes, etc.)
   */
  async loginToOrg(orgSlug, options) {
    this.setOrgContext(orgSlug);
    const scopes = options?.scopes ? [...this.config.scopes, ...options.scopes] : this.config.scopes;
    const state = generateState();
    const { verifier, challenge, method } = await generatePKCE();
    sessionStorage.setItem(this.getStorageKey(PKCE_STORAGE_KEY), verifier);
    sessionStorage.setItem(this.getStorageKey(STATE_STORAGE_KEY), state);
    const url = new URL(`/orgs/${orgSlug}/auth/login`, this.config.baseUrl);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", this.config.clientId);
    url.searchParams.set("redirect_uri", this.config.redirectUri);
    url.searchParams.set("scope", scopes.join(" "));
    url.searchParams.set("state", state);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", method);
    if (options?.prompt) {
      url.searchParams.set("prompt", options.prompt);
    }
    window.location.href = url.toString();
  }
  /**
   * Get organization session info.
   * Returns the current session for the specified organization.
   *
   * @param orgSlug - Organization slug
   * @returns Organization session or null if not logged in
   */
  async getOrgSession(orgSlug) {
    try {
      const token = await this.getAccessToken();
      if (!token) return null;
      const response = await fetch(`${this.config.apiUrl}/orgs/${orgSlug}/auth/session`, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        }
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 404) {
          return null;
        }
        throw new Error("Failed to get org session");
      }
      return await response.json();
    } catch {
      return null;
    }
  }
  /**
   * Get available OAuth providers for an organization.
   * Returns the list of OAuth providers enabled for the organization.
   *
   * @param orgSlug - Organization slug
   * @returns List of OAuth providers
   */
  async getOrgOAuthProviders(orgSlug) {
    try {
      const response = await fetch(`${this.config.apiUrl}/orgs/${orgSlug}/auth/oauth/providers`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (!response.ok) {
        return [];
      }
      return await response.json();
    } catch {
      return [];
    }
  }
  /**
   * Start OAuth login for an organization.
   * Redirects to the OAuth provider for org-specific login.
   *
   * @param orgSlug - Organization slug
   * @param provider - OAuth provider ID (e.g., 'google', 'github')
   * @param options - Login options
   */
  async loginToOrgWithOAuth(orgSlug, provider, options) {
    this.setOrgContext(orgSlug);
    const scopes = options?.scopes ? [...this.config.scopes, ...options.scopes] : this.config.scopes;
    const state = generateState();
    const { verifier, challenge, method } = await generatePKCE();
    sessionStorage.setItem(this.getStorageKey(PKCE_STORAGE_KEY), verifier);
    sessionStorage.setItem(this.getStorageKey(STATE_STORAGE_KEY), state);
    const url = new URL(`/orgs/${orgSlug}/auth/oauth/${provider}`, this.config.baseUrl);
    url.searchParams.set("response_type", "code");
    url.searchParams.set("client_id", this.config.clientId);
    url.searchParams.set("redirect_uri", this.config.redirectUri);
    url.searchParams.set("scope", scopes.join(" "));
    url.searchParams.set("state", state);
    url.searchParams.set("code_challenge", challenge);
    url.searchParams.set("code_challenge_method", method);
    if (options?.prompt) {
      url.searchParams.set("prompt", options.prompt);
    }
    window.location.href = url.toString();
  }
  /**
   * Logout from an organization context.
   *
   * @param orgSlug - Organization slug
   * @param options - Logout options
   */
  async logoutFromOrg(orgSlug, options) {
    try {
      const token = await this.getAccessToken();
      if (token) {
        await fetch(`${this.config.apiUrl}/orgs/${orgSlug}/auth/logout`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });
      }
    } catch {
    }
    if (this.orgSlug === orgSlug) {
      this.setOrgContext(null);
    }
    if (options?.returnTo) {
      window.location.href = options.returnTo;
    }
  }
  // =============================================================================
  // Organization Invitation Methods
  // =============================================================================
  /**
   * Validate an organization invitation token.
   *
   * Checks if the token is valid and returns the invitation details.
   * Does not require authentication.
   *
   * @param token - Invitation token from the invite URL
   * @returns Validation result with invite details or error
   *
   * @example
   * ```ts
   * const result = await client.validateInvite('abc123')
   * if (result.valid && result.invite) {
   *   console.log(`Invited to: ${result.invite.organizationName}`)
   *   console.log(`By: ${result.invite.invitedBy.name}`)
   * }
   * ```
   */
  async validateInvite(token) {
    return validateInvite(this.config.apiUrl, token);
  }
  /**
   * Accept an organization invitation.
   *
   * Requires authentication. The authenticated user will be added to the
   * organization with the roles specified in the invitation.
   *
   * @param token - Invitation token from the invite URL
   * @returns Accept result with organization details or error
   *
   * @example
   * ```ts
   * if (client.isAuthenticated()) {
   *   const result = await client.acceptInvite('abc123')
   *   if (result.success) {
   *     console.log(`Joined: ${result.organizationSlug}`)
   *   }
   * } else {
   *   // Redirect to login first
   *   await client.login()
   * }
   * ```
   */
  async acceptInvite(token) {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return {
        success: false,
        error: "Not authenticated. Please login first."
      };
    }
    return acceptInvite(this.config.apiUrl, token, accessToken);
  }
  /**
   * Get the base URL for the core app
   */
  get baseUrl() {
    return this.config.baseUrl;
  }
};

// src/silent-auth.ts
async function performSilentAuth(config) {
  const { baseUrl, clientId, redirectUri, scopes, timeout = 5e3 } = config;
  return new Promise((resolve) => {
    const state = generateState();
    let verifier;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.setAttribute("aria-hidden", "true");
    let timeoutId;
    let resolved = false;
    const cleanup = () => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timeoutId);
      window.removeEventListener("message", handleMessage);
      iframe.remove();
    };
    const handleMessage = (event) => {
      if (!event.origin.startsWith(baseUrl)) return;
      if (event.data?.type !== "authorization_response") return;
      if (event.data.state !== state) return;
      cleanup();
      if (event.data.error) {
        resolve(null);
        return;
      }
      if (event.data.code) {
        resolve({ code: event.data.code, verifier });
      } else {
        resolve(null);
      }
    };
    window.addEventListener("message", handleMessage);
    timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeout);
    generatePKCE().then(({ verifier: v, challenge, method }) => {
      verifier = v;
      const url = new URL("/silent-auth", baseUrl);
      url.searchParams.set("response_type", "code");
      url.searchParams.set("client_id", clientId);
      url.searchParams.set("redirect_uri", redirectUri);
      url.searchParams.set("scope", scopes.join(" "));
      url.searchParams.set("state", state);
      url.searchParams.set("code_challenge", challenge);
      url.searchParams.set("code_challenge_method", method);
      url.searchParams.set("prompt", "none");
      iframe.src = url.toString();
      document.body.appendChild(iframe);
    });
  });
}

// src/index.ts
var VERSION = "0.1.0";
export {
  AuthStateManager,
  TokenStore,
  USoftwareClient,
  VERSION,
  acceptInvite,
  decodeJwt,
  extractAppUserFromAccessToken,
  extractUserFromIdToken,
  generateCodeChallenge,
  generateCodeVerifier,
  generatePKCE,
  generateState,
  isJwtExpired,
  openAgreementPopup,
  openPopup,
  openStepUpPopup,
  performSilentAuth,
  validateInvite
};
//# sourceMappingURL=index.js.map